import {
    type Absence,
    dateAdd,
    formatRange,
    GetBalancesParams,
    GetCommonWorkDaysParams,
    getNextDay,
    getPrevDay,
    getRange,
    GetRosterNotesParams,
    getToday,
    getVacationEntitlement,
    GLOBAL_CONSTANTS,
    parseDateString,
    RequestAbsenceParams,
    type RosterNoteDTO,
    TimeEntryType,
    timeEntryTypeColor,
    UpdateAbsenceRequestParams,
    type VacationBalance,
    VacationPolicy,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import "../../components/roster-notes/roster-note";
import { app } from "../../init";
import { alert } from "../alert-dialog";
import { DateInput } from "../date-input";
import { Dialog } from "../dialog";

import { Task } from "@lit/task";
import { createRef, type Ref, ref } from "lit/directives/ref.js";

@customElement("ptc-staff-request-absence-dialog")
export class RequestAbsenceDialog extends Dialog<Absence, boolean> {
    readonly preventDismiss = true;

    @state()
    private _absence?: Absence;

    @state()
    private _commonWorkDays: number[] = [];

    @state()
    private _rosterNotes: RosterNoteDTO[] = [];

    @state()
    private _from?: DateString = getToday();

    @state()
    private _to?: DateString = getNextDay(getToday());

    /** used for display while refetching the balances */
    @state()
    private _cachedBalances: VacationBalance[] = [];

    private _startInput: Ref<DateInput> = createRef();

    private _endInput: Ref<DateInput> = createRef();

    private _formRef: Ref<HTMLFormElement> = createRef();

    async show(absence: Absence) {
        this._absence = absence;

        const fallbackDefaultDate = this._absence.type === TimeEntryType.Vacation ? getNextDay(getToday()) : getToday();

        this._from = absence.start || fallbackDefaultDate;
        this._to = absence.end || getNextDay(fallbackDefaultDate);

        this.loading = true;
        const showing = super.show();

        [this._rosterNotes, this._commonWorkDays] = await Promise.all([
            this._loadRosterNotes(),
            this._loadCommonWorkDays(),
        ]);

        this.loading = false;

        this._formRef.value?.setAttribute("tabindex", "0");
        this._formRef.value?.focus();

        return showing;
    }
    async hide() {
        super.hide();
    }

    private get _data() {
        if (!this._formRef.value) {
            return;
        }

        const data = new FormData(this._formRef.value);

        return {
            start: data.get("start") as DateString,
            end: data.get("end") ? dateAdd(data.get("end") as DateString, { days: 1 }) : undefined,
            message: data.get("message") as string,
        };
    }

    private get _days() {
        if (!this._data) {
            return [];
        }
        const { start, end } = this._data;

        if (!start || !end) {
            return [];
        }

        const days: string[] = [];
        let date = start;

        while (date < end) {
            days.push(date);
            date = dateAdd(date, { days: 1 });
        }

        return days;
    }

    private get _type() {
        return this._absence?.type || TimeEntryType.Vacation;
    }

    private async _loadRosterNotes() {
        if (!app.profile) {
            return [];
        }

        if (this._absence?.type === TimeEntryType.Vacation) {
            return await app.api.getRosterNotes(
                new GetRosterNotesParams({
                    from: this._from,
                    to: dateAdd(getToday(), {
                        months: GLOBAL_CONSTANTS.VACATION_FUTURE_LIMIT_MONTHS,
                    }),
                    filters: [{ type: "employeeId", value: app.profile.id }],
                })
            );
        } else {
            return [];
        }
    }
    private async _loadCommonWorkDays() {
        if (!app.profile) {
            return [];
        }

        return await app.api.getCommonWorkDays(
            new GetCommonWorkDaysParams({
                employeeId: app.profile.id,
                before: this._from,
            })
        );
    }

    private _vacationBalanceTask = new Task(this, {
        task: async ([from, to]) => {
            if (!from || !to || !app.profile) throw new Error("Missing required parameters");

            const balanceFrom = getRange(from, "year").from;
            const balanceTo = getRange(to, "year").to;

            const balances = await app.api.getVacationBalances(
                new GetBalancesParams({
                    filters: [{ type: "employeeId", value: app.profile.id }],
                    from: balanceFrom,
                    to: balanceTo,
                    resolution: "year",
                })
            );

            if (balances.length > 0) {
                this._cachedBalances = balances;
                return balances;
            }

            return [];
        },

        args: () => [this._from || this._absence?.start, this._to || this._absence?.end],
    });

    private _isCommonDay(date: string) {
        if (!app.profile) {
            return false;
        }
        const d = parseDateString(date)!;
        const contract = app.profile.getContractForDate(date);
        if (contract && contract.fixedWorkDays) {
            return !!contract.hoursPerDay && contract.hoursPerDay[(d.getDay() + 6) % 7] !== 0;
        }
        return this._commonWorkDays.includes(d.getDay());
    }

    renderContent() {
        const data = this._data;
        const type = this._type;

        const blockingRosterNotes =
            (type === TimeEntryType.Vacation &&
                data?.start &&
                data?.end &&
                this._rosterNotes
                    .filter(
                        (n) =>
                            n.start < data!.end! && n.end > data!.start! && n.vacationPolicy !== VacationPolicy.AllowAll
                    )
                    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())) ||
            [];

        const datePickerBadge = (date: string) => {
            const blocked = this._rosterNotes.find(
                (n) => n.start <= date && n.end > date && n.vacationPolicy !== VacationPolicy.AllowAll
            );
            return blocked
                ? {
                      icon: "icon.status.blocked",
                      class: "red",
                  }
                : undefined;
        };

        const minFromDate = dateAdd(getToday(), {
            // -3 because the employee can add sick leave on the 3rd day at the latest and possible only then knows the date
            days: type === TimeEntryType.Vacation ? 1 : -3,
        });

        return html`
            <form ${ref(this._formRef)} @submit=${this._submit} class="padded" @input=${() => this.requestUpdate()}>
                <div class="padded margined relative">
                    <div class="big colored-text text-centering" style="--color-highlight: ${timeEntryTypeColor(type)}">
                        <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(type)}"></ptc-ui-icon>
                        ${type === TimeEntryType.Vacation ? "Urlaub Beantragen" : "Krankmeldung"}
                    </div>
                </div>

                <div class="margined">
                    <div class="horizontal evenly stretching spacing layout">
                        <div class="vertical layout">
                            <label>Von</label>
                            <ptc-date-input
                                ${ref(this._startInput)}
                                name="start"
                                required
                                @change=${this._startChanged}
                                .min=${minFromDate}
                                .value=${this._from}
                                datePicker="popover"
                                .datePickerBadge=${datePickerBadge}
                            ></ptc-date-input>
                        </div>
                        <div class="vertical layout">
                            <label>Bis</label>
                            <ptc-date-input
                                ${ref(this._endInput)}
                                name="end"
                                required
                                @change=${this._endChanged}
                                .min=${this._from}
                                .value=${this._to ? getPrevDay(this._to) : ""}
                                datePicker="popover"
                                .datePickerBadge=${datePickerBadge}
                            ></ptc-date-input>
                        </div>
                    </div>

                    ${blockingRosterNotes.length
                        ? html`
                              <div class="double-margined">
                                  <div class="red margined text-centering colored-text">
                                      <ptc-ui-icon name="icon.status.blocked"></ptc-ui-icon> Urlaubssperre für den
                                      gewählten Zeitraum!
                                  </div>
                                  <div class="wrapping spacing layout vertical">
                                      ${blockingRosterNotes.map(
                                          (note) => html`
                                              <ptc-roster-note
                                                  class="small"
                                                  .rosterNote=${note}
                                                  .employee=${app.profile}
                                              ></ptc-roster-note>
                                          `
                                      )}
                                  </div>
                              </div>
                          `
                        : type === TimeEntryType.Vacation
                          ? this._renderVacation()
                          : nothing}

                    <div>
                        <label>Bemerkungen</label>
                        <textarea name="message" class="fill-horizontally"></textarea>
                    </div>
                </div>
                <div class="padded spacing evenly stretching horizontal layout">
                    <button class="primary" ?disabled=${Boolean(blockingRosterNotes.length)}>Abschicken</button>
                    <button class="transparent" type="button" @click=${() => this.done()}>Abbrechen</button>
                </div>
            </form>
        `;
    }

    private _renderVacation() {
        return html` <div class="double-margined stats centering horizontal layout text-centering">
            ${this._from && this._to
                ? this._vacationBalanceTask.render({
                      initial: () => html`<p>Resturlaub</p>`,
                      error: () => html`<p>Fehler beim Laden des Resturlaubes</p>`,
                      pending: () => this._cachedBalances?.map((balance) => this._renderVacationBalance(balance)),
                      complete: (balances) => balances.map((balance) => this._renderVacationBalance(balance)),
                  })
                : nothing}
        </div>`;
    }

    private _renderVacationBalance(balance: VacationBalance) {
        const vacationEntitlement = getVacationEntitlement(balance);
        const daysRequired = this._days.reduce(
            (total, date) => (date >= balance.from && date < balance.to && this._isCommonDay(date) ? total + 1 : total),
            0
        );

        if (!daysRequired) return;

        const daysLeft = Math.floor(vacationEntitlement.remaining - daysRequired);

        return html`
            <div class="double-margined stats centering horizontal layout text-centering">
                <div class="horizontally-margined">
                    <div class="semibold blue colored-text stats-label">Resturlaub ${formatRange(balance)}</div>
                    <div class="huge">
                        ${Math.floor(vacationEntitlement.remaining)}
                        <ptc-ui-icon name="icon.navigation.forward" class="smaller"></ptc-ui-icon>
                        <span class="colored-text ${daysLeft < 0 ? "red" : "green"}">${daysLeft} </span>
                    </div>
                </div>
            </div>
        `;
    }

    private async _submit(e: FocusEvent) {
        e.preventDefault();

        if (!this._absence) {
            return;
        }

        if (!(this._startInput.value?.reportValidity() && this._endInput.value?.reportValidity())) {
            return;
        }

        const { start, end, message } = this._data || {};

        this.loading = true;

        try {
            if (this._absence.id) {
                await app.api.updateAbsenceRequest(
                    new UpdateAbsenceRequestParams({ id: this._absence.id, start, end, message })
                );
            } else {
                await app.api.requestAbsence(
                    new RequestAbsenceParams({
                        start,
                        end: end!,
                        message,
                        type: this._absence.type as TimeEntryType.Sick | TimeEntryType.Vacation,
                    })
                );
            }
            this.done(true);
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this.loading = false;
    }

    private _startChanged(ev: InputEvent) {
        const value = (ev.currentTarget as DateInput)?.value || undefined;

        this._from = value;
    }
    private _endChanged(ev: InputEvent) {
        const value = (ev.currentTarget as DateInput)?.value || undefined;

        value && (this._to = getNextDay(value));
    }

    static styles = [
        ...Dialog.styles,
        DateInput.styles,
        css`
            .inner {
                max-width: 30em;
            }
        `,
    ];
}
